import { Braces, Eraser, PenLine, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type GeoJSONStoreFeatures,
  TerraDraw,
  TerraDrawLineStringMode,
  TerraDrawPolygonMode,
} from 'terra-draw';
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter';
import { safeHex } from '@/lib/colors';
import { cleanGeometry, type ShapeGeometry } from '@/lib/geojson';
import { t } from '@/lib/i18n';
import { Button } from '../ui/button';
import { GeoJsonDialog } from './geojson-dialog';
import { useMap } from './map-view';
import { VertexEditor } from './vertex-editor';

type Geometry = ShapeGeometry;
interface ShapeSlot {
  key: string;
  label: string;
  kind: 'LineString' | 'Polygon';
  color: string;
  value?: Geometry | null;
}

const round = (c: number[]) => [Math.round(c[0]! * 1e7) / 1e7, Math.round(c[1]! * 1e7) / 1e7];
function fromDrawing(geometry: GeoJSONStoreFeatures['geometry']): Geometry | null {
  if (geometry.type === 'LineString')
    return { type: 'LineString', coordinates: geometry.coordinates.map(round) as never };
  if (geometry.type === 'Polygon')
    return { type: 'Polygon', coordinates: geometry.coordinates.map((r) => r.map(round)) as never };
  return null;
}

/**
 * Lines and polygons on the map. Each slot holds one shape (e.g. a zone's "going" and "return"
 * fences). The active slot's points can be dragged right away; new shapes are drawn with terra-draw.
 */
export function ShapeEditor({
  slots,
  onChange,
  readOnly = false,
}: {
  slots: ShapeSlot[];
  onChange: (key: string, value: Geometry | null) => void;
  /** Only shows the shapes (no drawing tools, no dragging points). */
  readOnly?: boolean;
}) {
  const map = useMap();
  const draw = useRef<TerraDraw | null>(null);
  const [active, setActive] = useState(slots[0]!.key);
  const [drawing, setDrawing] = useState(false);
  const [json, setJson] = useState(false);
  const change = useRef(onChange);
  change.current = onChange;
  const activeRef = useRef(active);
  activeRef.current = active;
  const slotsRef = useRef(slots);
  slotsRef.current = slots;

  // terra-draw is only used while drawing a new shape; it stays in its inert "static" mode otherwise.
  useEffect(() => {
    if (!map) return;
    const colorOf = () =>
      safeHex(slotsRef.current.find((s) => s.key === activeRef.current)?.color, '#0b3f82');
    const td = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [
        new TerraDrawPolygonMode({
          styles: { fillColor: colorOf, outlineColor: colorOf, fillOpacity: 0.25, outlineWidth: 2 },
        }),
        new TerraDrawLineStringMode({ styles: { lineStringColor: colorOf, lineStringWidth: 4 } }),
      ],
    });
    td.start();
    td.setMode('static');
    td.on('finish', (id, ctx) => {
      if (ctx.action !== 'draw') return;
      const g = fromDrawing(td.getSnapshotFeature(id)!.geometry);
      td.removeFeatures([id]);
      td.setMode('static');
      setDrawing(false);
      if (g) change.current(activeRef.current, g);
    });
    draw.current = td;
    return () => {
      draw.current = null;
      try {
        td.stop();
      } catch {}
    };
  }, [map]);

  const slot = slots.find((s) => s.key === active)!;
  const startDrawing = () => {
    draw.current?.setMode(slot.kind === 'Polygon' ? 'polygon' : 'linestring');
    setDrawing(true);
  };
  const cancel = () => {
    draw.current?.clear();
    draw.current?.setMode('static');
    setDrawing(false);
  };

  const jsonButton = (
    <Button
      size="sm"
      variant="secondary"
      icon={<Braces className="size-4" />}
      onClick={() => setJson(true)}
      disabled={drawing}
      title={t('View, edit, import or export the shape (GeoJSON, KML, KMZ)')}
    >
      GeoJSON
    </Button>
  );

  return (
    <>
      <GeoJsonDialog
        open={json}
        onClose={() => setJson(false)}
        kind={slot.kind}
        name={slot.label}
        value={slot.value}
        color={slot.color}
        readOnly={readOnly}
        onApply={(g) => change.current(active, g)}
      />
      {readOnly && slot.value && (
        <div className="absolute top-2 left-2 z-10 rounded-lg bg-white/95 p-2 shadow-md ring-1 ring-slate-200">
          {jsonButton}
        </div>
      )}
      {slots.map((s) => (
        <SlotShape
          key={s.key}
          slot={s}
          editable={!readOnly && s.key === active && !drawing}
          onChange={(next) => change.current(s.key, next)}
        />
      ))}
      {!readOnly && (
        <div className="absolute top-2 left-2 z-10 flex flex-col gap-2 rounded-lg bg-white/95 p-2 shadow-md ring-1 ring-slate-200">
          {slots.length > 1 && (
            <div className="flex gap-1">
              {slots.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setActive(s.key)}
                  disabled={drawing}
                  className={`flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium ${active === s.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                >
                  <span className="size-2.5 rounded-full" style={{ background: s.color }} />
                  {s.label}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-1">
            {drawing ? (
              <Button size="sm" variant="secondary" icon={<Undo2 className="size-4" />} onClick={cancel}>
                {t('Cancel')}
              </Button>
            ) : (
              <Button
                size="sm"
                variant={slot.value ? 'secondary' : 'primary'}
                icon={<PenLine className="size-4" />}
                onClick={startDrawing}
              >
                {slots.length > 1
                  ? slot.value
                    ? t('Redraw {shape}', { shape: slot.label.toLowerCase() })
                    : t('Draw {shape}', { shape: slot.label.toLowerCase() })
                  : slot.kind === 'Polygon'
                    ? slot.value
                      ? t('Redraw area')
                      : t('Draw area')
                    : slot.value
                      ? t('Redraw route')
                      : t('Draw route')}
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              icon={<Eraser className="size-4" />}
              onClick={() => change.current(active, null)}
              disabled={!slot.value || drawing}
            >
              {t('Clear')}
            </Button>
            {jsonButton}
          </div>
          <p className="max-w-60 text-[11px] leading-snug text-slate-500">
            {drawing
              ? slot.kind === 'Polygon'
                ? t('Click to add corners, click the first corner to close.')
                : t('Click to add points, double-click to finish.')
              : slot.value
                ? t(
                    'Drag a point to move it, drag a small white point to add one, right-click a point to delete it.',
                  )
                : t('Draw the shape on the map.')}
          </p>
        </div>
      )}
    </>
  );
}

function SlotShape({
  slot,
  editable,
  onChange,
}: {
  slot: ShapeSlot;
  editable: boolean;
  onChange: (g: Geometry) => void;
}) {
  const geometry = useMemo(() => cleanGeometry(slot.value), [slot.value]);
  return geometry ? (
    <VertexEditor
      geometry={geometry}
      color={safeHex(slot.color, '#0b3f82')}
      editable={editable}
      onChange={onChange}
    />
  ) : null;
}
